import React from 'react';
import { useDispatch } from 'react-redux';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { Button } from '@interlayer/js/components/common/Button';
import { Input } from '@interlayer/js/components/common/Fields/Input/Input';
import Form from '@interlayer/js/components/common/Form/Form';
import { I18n } from '@interlayer/js/components/common/I18n';

import { useTranslations } from '@core/js/hooks/i18n';
import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { clsb } from '@core/js/utils/classNames.util';

import { useInMobile } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useGetGeocode } from '@interlayer/js/app/hooks/postCodeFinder/hooks/useGeocode';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { usePostCodeSelector } from '@interlayer/js/app/store/hooks/usePostCodeSelector';
import { setClubData } from '@interlayer/js/app/store/reducers/postCodeFinder/actions';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useNavigate } from '@interlayer/js/hooks/navigate';

import { TClubSearchShort } from './types';

import style from './ClubSearchShort.scss';

export const ClubSearchShort: React.FC<TClubSearchShort> = ({
    isSearchBlockOnNewLine,
    isSearchFromNavigation,
    classNames = {}
}) => {
    const navigate = useNavigate();
    const isMobile = useInMobile();
    const dispatch = useDispatch();
    const showNotification = useShowNotification();
    const { fetch: getClubByQuery } = useGetGeocode();
    const { isLoading, clubData: { name: clubName } } = usePostCodeSelector();
    const t = useTranslations('playtech.mobile.system.club.');
    const [
        dTagClubsSearchResultUrl = '/clubs/search-results',
        dTagClubsSearchResultAzUrl = '/clubs/search-results-az'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.clubsSearchResultUrl,
        DYNAMIC_TAG_NAMES.clubsSearchResultAzUrl
    ]);
    const isOnResultPage = location.pathname === dTagClubsSearchResultUrl;
    const buildCls = clsb([style, classNames]);

    const resetClubData = () => {
        dispatch(setClubData({
            name: ''
        }));
    };

    const handlerSubmit = ({ query }: { query: string }) => {
        if (!query) {
            resetClubData();
            navigate(dTagClubsSearchResultUrl);
            return;
        }

        return getClubByQuery({ paramsFetch: query })
            .then(() => {
                if (!isOnResultPage) {
                    navigate(dTagClubsSearchResultUrl);
                }
            })
            .catch(() => showNotification(t('error.general')));
    };

    const handleAZResult = () => {
        resetClubData();
        navigate(dTagClubsSearchResultAzUrl);
    };

    if (isSearchFromNavigation) {
        return (
            <Form
                onSubmit={handlerSubmit}
                render={({ submitting, handleSubmit, pristine, values: { query } }) => (
                    <form
                        data-testid="ClubSearchShort_navigation"
                        className={
                            clsx(
                                buildCls(['form']),
                                { [style.isSubmitting]: submitting },
                                { [style.isSearchBlockOnNewLine]: isSearchBlockOnNewLine }
                            )
                        }
                        onSubmit={handleSubmit}
                    >
                        <Input
                            name="query"
                            defaultValue={clubName}
                            classNames={{
                                control: style.inputControl,
                                content: style.content
                            }}
                            placeholder={t('placeholder.shortSearch')}
                            leftBlock={<SvgIcon className={style.icon} type="search-thin" />}
                            isHideForcedPlaceholder
                        />

                        <div className={style.content}>
                            <Button
                                type="submit"
                                className={buildCls(['btnSubmit'])}
                                data-testid="ClubSearchShort_buttonSearch"
                                disabled={submitting || pristine || !query.length || isLoading}
                            >
                                {t('button.search')}
                            </Button>
                        </div>
                    </form>
                )}
            />
        );
    }

    return (
        <Form
            onSubmit={handlerSubmit}
            render={({ submitting, handleSubmit, pristine }) => (
                <form
                    data-testid="ClubSearchShort_clubSearch"
                    className={clsx(buildCls(['form']), buildCls(['formSearchBlock']))}
                    onSubmit={handleSubmit}
                >
                    <I18n
                        t={t}
                        tKey="title.clubfinder"
                        className={style.title}
                    />

                    <Input
                        name="query"
                        defaultValue=""
                        classNames={{ control: style.inputControl }}
                        placeholder={t(isMobile ? 'placeholder.search' : 'placeholder.fullSearch')}
                    />

                    <Button
                        type="submit"
                        className={style.btn}
                        data-testid="ClubSearchShort_buttonSearch"
                        disabled={submitting || pristine || isLoading}
                    >
                        {t('button.search')}
                    </Button>

                    <Button
                        data-testid="ClubSearchShort_linkAz"
                        size="medium"
                        view="link"
                        className={style.link}
                        onClick={handleAZResult}
                    >
                        {t('link.az')}
                    </Button>
                </form>
            )}
        />
    );
};
